import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
    Card,
    CardContent,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Check, Copy, Terminal, Server, Database, Shield, Zap, ArrowRight, Code2 } from "lucide-react";
import { NavigationHeader } from "@/components/NavigationHeader";

export default function ApiDocs() {
    const [copied, setCopied] = useState(false);

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const ExampleResponse = JSON.stringify({
        "generatedAt": "2024-03-20T14:30:00.000Z",
        "councilors": [
            {
                "id": "abc12345",
                "name": "Maria Silva",
                "party": "PDB",
                "photo": "https://...",
                "active": true
            }
        ],
        "institutions": [
            {
                "id": "inst123",
                "name": "Hospital Santa Casa",
                "cnpj": "12345678000100",
                "category": "SAÚDE"
            }
        ],
        "amendments": [
            {
                "id": "xyz98765",
                "officialCode": "001-025-2024",
                "value": 50000.00,
                "description": "Reforma da quadra escolar...",
                "status": "Aprovada",
                "year": "2024"
            }
        ]
    }, null, 2);

    return (
        <div className="min-h-screen bg-white text-slate-900">
            <NavigationHeader />

            {/* Hero Section */}
            <div className="relative overflow-hidden bg-gradient-to-b from-blue-50 to-white py-24 sm:py-32">
                <div className="absolute inset-0 bg-[url('/grid.svg')] bg-center [mask-image:linear-gradient(180deg,white,rgba(255,255,255,0))] opacity-40"></div>
                <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
                    <div className="mx-auto max-w-2xl text-center">
                        <div className="flex justify-center mb-6">
                            <Badge variant="secondary" className="px-4 py-1.5 text-sm rounded-full bg-blue-100 text-blue-700 border-blue-200">
                                Versão 1.0 Disponível
                            </Badge>
                        </div>
                        <h1 className="text-4xl font-bold tracking-tight text-slate-900 sm:text-6xl">
                            API RESTful de Dados Abertos
                        </h1>
                        <p className="mt-6 text-lg leading-8 text-slate-600">
                            Acesse dados de emendas parlamentares, vereadores e instituições em tempo real.
                            Simples, segura e documentada.

                        </p>
                        <div className="mt-10 flex items-center justify-center gap-x-6">
                            <Link to="/admin" className="rounded-md bg-blue-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 transition-all flex items-center gap-2">
                                <Shield className="w-4 h-4" />
                                Área do Desenvolvedor
                            </Link>
                            <a href="#docs" className="text-sm font-semibold leading-6 text-slate-900 flex items-center gap-1 hover:text-blue-600 transition-colors">
                                Ver Documentação <ArrowRight className="w-4 h-4" />
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            <main id="docs" className="container mx-auto py-12 px-4 lg:py-20 max-w-7xl">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">

                    {/* Docs Navigation / Sidebar */}
                    <div className="hidden lg:block lg:col-span-3 space-y-8">
                        <div className="sticky top-24">
                            <h3 className="font-semibold text-slate-900 border-b pb-2 mb-4">Índice</h3>
                            <nav className="space-y-1">
                                <a href="#intro" className="block px-3 py-2 text-sm font-medium text-slate-600 rounded-md hover:bg-slate-50 hover:text-blue-600 transition-colors">Introdução</a>
                                <a href="#auth" className="block px-3 py-2 text-sm font-medium text-slate-600 rounded-md hover:bg-slate-50 hover:text-blue-600 transition-colors">Autenticação</a>
                                <a href="#endpoints" className="block px-3 py-2 text-sm font-medium text-slate-600 rounded-md hover:bg-slate-50 hover:text-blue-600 transition-colors">Endpoints</a>
                                <a href="#schemas" className="block px-3 py-2 text-sm font-medium text-slate-600 rounded-md hover:bg-slate-50 hover:text-blue-600 transition-colors">Modelos de Dados</a>
                            </nav>

                            <div className="mt-8 p-4 bg-blue-50 rounded-xl border border-blue-100">
                                <h4 className="font-semibold text-blue-900 mb-2 text-sm flex items-center gap-2">
                                    <Zap className="w-4 h-4 text-blue-600" /> Dica Pro
                                </h4>
                                <p className="text-xs text-blue-700 leading-relaxed">
                                    Utilize o header <code>Accept-Encoding: gzip</code> para reduzir o tráfego de dados em respostas grandes.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Main Content */}
                    <div className="col-span-1 lg:col-span-9 space-y-16">

                        {/* Introduction */}
                        <section id="intro" className="space-y-6">
                            <div>
                                <h2 className="text-3xl font-bold tracking-tight text-slate-900">Introdução</h2>
                                <p className="mt-4 text-lg text-slate-600">
                                    A API de Dados Públicos permite que sistemas de terceiros consumam informações oficiais sobre emendas parlamentares, execução orçamentária e dados institucionais de forma programática.
                                </p>
                            </div>
                            <div className="grid sm:grid-cols-3 gap-6">
                                <Card className="bg-white border-slate-200 hover:border-blue-200 transition-colors shadow-sm">
                                    <CardContent className="pt-6">
                                        <Database className="w-8 h-8 text-blue-600 mb-2" />
                                        <h3 className="font-semibold mb-1 text-slate-900">Dados em Tempo Real</h3>
                                        <p className="text-sm text-slate-500">Acesso direto à base de dados atualizada.</p>
                                    </CardContent>
                                </Card>
                                <Card className="bg-white border-slate-200 hover:border-blue-200 transition-colors shadow-sm">
                                    <CardContent className="pt-6">
                                        <Server className="w-8 h-8 text-blue-600 mb-2" />
                                        <h3 className="font-semibold mb-1 text-slate-900">Alta Disponibilidade</h3>
                                        <p className="text-sm text-slate-500">Infraestrutura escalável baseada em nuvem.</p>
                                    </CardContent>
                                </Card>
                                <Card className="bg-white border-slate-200 hover:border-blue-200 transition-colors shadow-sm">
                                    <CardContent className="pt-6">
                                        <Code2 className="w-8 h-8 text-blue-600 mb-2" />
                                        <h3 className="font-semibold mb-1 text-slate-900">Padrão JSON</h3>
                                        <p className="text-sm text-slate-500">Respostas formatadas em JSON padrão.</p>
                                    </CardContent>
                                </Card>
                            </div>
                        </section>

                        {/* Authentication */}
                        <section id="auth" className="space-y-8 scroll-mt-24">
                            <div className="border-b pb-4">
                                <h2 className="text-2xl font-bold tracking-tight text-slate-900">Autenticação e Acesso</h2>
                            </div>

                            <div className="space-y-4">
                                <h3 className="text-lg font-semibold text-slate-900">1. Como funciona</h3>
                                <p className="text-slate-600 leading-relaxed">
                                    A API utiliza o padrão <strong>Bearer Token</strong> para autenticação. Isso significa que todas as suas requisições devem incluir um cabeçalho HTTP <code>Authorization</code> contendo o token fornecido.
                                    <br />
                                    Este token é único, secreto e identifica sua aplicação no sistema.
                                </p>
                            </div>

                            <Card className="bg-slate-900 border-slate-800 text-slate-300 overflow-hidden shadow-lg">
                                <div className="flex items-center justify-between px-4 py-2 bg-slate-950 border-b border-slate-800">
                                    <div className="flex items-center gap-2">
                                        <Terminal className="w-4 h-4 text-slate-500" />
                                        <span className="text-xs font-mono text-slate-500">Exemplo de Requisição</span>
                                    </div>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-6 w-6 text-slate-400 hover:text-white hover:bg-slate-800"
                                        onClick={() => copyToClipboard(`curl -X GET "${window.location.origin}/admin-api/api/public/v1/amendments?year=2024" \\\n  -H "Authorization: Bearer SEU_TOKEN_AQUI"`)}
                                    >
                                        {copied ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
                                    </Button>
                                </div>
                                <CardContent className="p-4 font-mono text-sm overflow-x-auto bg-slate-900">
                                    <div className="flex flex-col gap-2">
                                        <div>
                                            <span className="text-pink-400">curl</span> <span className="text-slate-400">-X GET</span> <span className="text-green-400">"{window.location.origin}/admin-api/api/public/v1/amendments?year=2024"</span> <span className="text-slate-500">\</span>
                                        </div>
                                        <div className="pl-4">
                                            <span className="text-slate-400">-H</span> <span className="text-yellow-300">"Authorization: Bearer &lt;SEU_TOKEN_AQUI&gt;"</span>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            <div className="space-y-6 pt-4">
                                <h3 className="text-lg font-semibold text-slate-900">2. Solicitando sua Chave (Token)</h3>

                                <div className="grid md:grid-cols-2 gap-6">
                                    <div className="bg-blue-50 border border-blue-100 rounded-lg p-5">
                                        <h4 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
                                            <Shield className="w-4 h-4" /> Para Administradores
                                        </h4>
                                        <p className="text-sm text-blue-700 mb-4">
                                            Se você é um administrador do sistema, pode gerar tokens instantaneamente através do painel de gestão.
                                        </p>
                                        <Link to="/admin/api-tokens">
                                            <Button variant="outline" size="sm" className="bg-white text-blue-700 border-blue-200 hover:bg-blue-50">
                                                Gerenciar Tokens &rarr;
                                            </Button>
                                        </Link>
                                    </div>

                                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-5">
                                        <h4 className="font-medium text-slate-900 mb-2 flex items-center gap-2">
                                            <Database className="w-4 h-4" /> Para Desenvolvedores Externos
                                        </h4>
                                        <p className="text-sm text-slate-600 mb-4">
                                            O acesso é restrito a parceiros autorizados. Para solicitar uma chave, envie um e-mail para o departamento de TI com:
                                        </p>
                                        <ul className="list-disc list-inside text-xs text-slate-500 space-y-1 ml-1">
                                            <li>Nome do Projeto / Instituição</li>
                                            <li>Propósito da integração</li>
                                            <li>Responsável técnico</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-4 pt-4 border-t border-slate-100">
                                <h3 className="text-lg font-semibold text-slate-900">3. Filtrando Resultados (Query Params)</h3>
                                <p className="text-slate-600 leading-relaxed">
                                    A maioria dos endpoints de listagem suporta filtragem via <strong>Query Parameters</strong> na URL.
                                    Você pode combinar múltiplos filtros usando o caractere <code>&</code>.
                                </p>
                                <div className="bg-slate-50 rounded-md p-4 border border-slate-200">
                                    <p className="font-mono text-sm text-slate-700 mb-2">Exemplo: Buscar emendas do ano <strong>2024</strong> com valor acima de <strong>R$ 50.000</strong>:</p>
                                    <code className="block bg-white p-2 rounded border border-slate-200 text-blue-600 font-mono text-xs break-all">
                                        /api/public/v1/amendments<span className="text-pink-600">?year=2024</span><span className="text-slate-400">&</span><span className="text-pink-600">min_value=50000</span>
                                    </code>
                                </div>
                            </div>
                        </section>

                        {/* Endpoints */}
                        <section id="endpoints" className="space-y-8 scroll-mt-24">
                            <div className="border-b pb-4">
                                <h2 className="text-2xl font-bold tracking-tight text-slate-900">Endpoints</h2>
                            </div>

                            {/* Endpoint Item: Councilors */}
                            <div className="space-y-4 pt-8 border-t border-slate-100" id="endpoint-councilors">
                                <div className="flex items-center gap-4">
                                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 font-mono text-base px-3 py-1">GET</Badge>
                                    <code className="text-lg font-mono text-slate-700">/api/public/v1/councilors</code>
                                </div>
                                <p className="text-slate-600">
                                    Retorna a lista de parlamentares cadastrados, permitindo filtragem por status, partido e nome.
                                </p>

                                <Tabs defaultValue="parameters" className="w-full">
                                    <TabsList>
                                        <TabsTrigger value="parameters">Parâmetros</TabsTrigger>
                                        <TabsTrigger value="response">Exemplo de Resposta</TabsTrigger>
                                    </TabsList>
                                    <TabsContent value="parameters" className="mt-4">
                                        <Card className="bg-white border-slate-200">
                                            <div className="rounded-md border-b bg-slate-50 px-4 py-3 font-medium text-sm text-slate-700 grid grid-cols-12 gap-4">
                                                <div className="col-span-3">Parâmetro</div>
                                                <div className="col-span-2">Tipo</div>
                                                <div className="col-span-7">Descrição</div>
                                            </div>
                                            <div className="px-4 py-3 text-sm text-slate-600 grid grid-cols-12 gap-4 border-b">
                                                <div className="col-span-3 font-mono text-blue-600">active</div>
                                                <div className="col-span-2">boolean</div>
                                                <div className="col-span-7">Filtra por status de atividade ('true' ou 'false').</div>
                                            </div>
                                            <div className="px-4 py-3 text-sm text-slate-600 grid grid-cols-12 gap-4 border-b">
                                                <div className="col-span-3 font-mono text-blue-600">party</div>
                                                <div className="col-span-2">string</div>
                                                <div className="col-span-7">Filtra pelo partido (sigla exata).</div>
                                            </div>
                                            <div className="px-4 py-3 text-sm text-slate-600 grid grid-cols-12 gap-4">
                                                <div className="col-span-3 font-mono text-blue-600">name</div>
                                                <div className="col-span-2">string</div>
                                                <div className="col-span-7">Busca parcial pelo nome do parlamentar.</div>
                                            </div>
                                        </Card>
                                    </TabsContent>
                                    <TabsContent value="response" className="mt-4">
                                        <Card className="bg-slate-50 border-slate-200 shadow-sm">
                                            <ScrollArea className="h-[300px] w-full rounded-md">
                                                <pre className="p-4 text-sm font-mono leading-relaxed text-slate-800">
                                                    <code dangerouslySetInnerHTML={{ __html: JSON.stringify({ count: 1, data: [{ id: "...", name: "Maria Silva", party: "PDB", active: true }] }, null, 2).replace(/"key":/g, '<span class="text-blue-600">"key":</span>').replace(/: "string"/g, ': <span class="text-green-600">"string"</span>').replace(/: true/g, ': <span class="text-purple-600">true</span>') }} />
                                                </pre>
                                            </ScrollArea>
                                        </Card>
                                    </TabsContent>
                                </Tabs>
                            </div>

                            {/* Endpoint Item: Amendments */}
                            <div className="space-y-4 pt-8 border-t border-slate-100" id="endpoint-amendments">
                                <div className="flex items-center gap-4">
                                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 font-mono text-base px-3 py-1">GET</Badge>
                                    <code className="text-lg font-mono text-slate-700">/api/public/v1/amendments</code>
                                </div>
                                <p className="text-slate-600">
                                    Consulta detalhada de emendas com suporte a diversos filtros combinados (ano, vereador, instituição, valores, status).
                                </p>

                                <Tabs defaultValue="parameters" className="w-full">
                                    <TabsList>
                                        <TabsTrigger value="parameters">Parâmetros</TabsTrigger>
                                        <TabsTrigger value="response">Exemplo de Resposta</TabsTrigger>
                                    </TabsList>
                                    <TabsContent value="parameters" className="mt-4">
                                        <Card className="bg-white border-slate-200">
                                            <div className="rounded-md border-b bg-slate-50 px-4 py-3 font-medium text-sm text-slate-700 grid grid-cols-12 gap-4">
                                                <div className="col-span-3">Parâmetro</div>
                                                <div className="col-span-2">Tipo</div>
                                                <div className="col-span-7">Descrição</div>
                                            </div>
                                            <div className="px-4 py-3 text-sm text-slate-600 grid grid-cols-12 gap-4 border-b">
                                                <div className="col-span-3 font-mono text-blue-600">year</div>
                                                <div className="col-span-2">number</div>
                                                <div className="col-span-7">Ano de exercício da emenda.</div>
                                            </div>
                                            <div className="px-4 py-3 text-sm text-slate-600 grid grid-cols-12 gap-4 border-b">
                                                <div className="col-span-3 font-mono text-blue-600">councilorId</div>
                                                <div className="col-span-2">string</div>
                                                <div className="col-span-7">ID do parlamentar autor.</div>
                                            </div>
                                            <div className="px-4 py-3 text-sm text-slate-600 grid grid-cols-12 gap-4 border-b">
                                                <div className="col-span-3 font-mono text-blue-600">institutionId</div>
                                                <div className="col-span-2">string</div>
                                                <div className="col-span-7">ID da instituição beneficiária.</div>
                                            </div>
                                            <div className="px-4 py-3 text-sm text-slate-600 grid grid-cols-12 gap-4 border-b">
                                                <div className="col-span-3 font-mono text-blue-600">status</div>
                                                <div className="col-span-2">string</div>
                                                <div className="col-span-7">Situação (ex: 'Pago', 'Empenhada').</div>
                                            </div>
                                            <div className="px-4 py-3 text-sm text-slate-600 grid grid-cols-12 gap-4 border-b">
                                                <div className="col-span-3 font-mono text-blue-600">min_value</div>
                                                <div className="col-span-2">number</div>
                                                <div className="col-span-7">Valor mínimo da emenda (R$).</div>
                                            </div>
                                            <div className="px-4 py-3 text-sm text-slate-600 grid grid-cols-12 gap-4 border-b">
                                                <div className="col-span-3 font-mono text-blue-600">max_value</div>
                                                <div className="col-span-2">number</div>
                                                <div className="col-span-7">Valor máximo da emenda (R$).</div>
                                            </div>
                                            <div className="px-4 py-3 text-sm text-slate-600 grid grid-cols-12 gap-4">
                                                <div className="col-span-3 font-mono text-blue-600">search</div>
                                                <div className="col-span-2">string</div>
                                                <div className="col-span-7">Busca textual na descrição ou código oficial.</div>
                                            </div>
                                        </Card>
                                    </TabsContent>
                                    <TabsContent value="response" className="mt-4">
                                        <Card className="bg-slate-50 border-slate-200 shadow-sm">
                                            <ScrollArea className="h-[400px] w-full rounded-md">
                                                <pre className="p-4 text-sm font-mono leading-relaxed text-slate-800">
                                                    <code dangerouslySetInnerHTML={{ __html: JSON.stringify({ count: 1, filters: { year: "2024" }, data: [{ id: "...", officialCode: "001-025-2024", value: 50000, description: "Reforma...", status: "Pago" }] }, null, 2).replace(/"key":/g, '<span class="text-blue-600">"key":</span>').replace(/: "string"/g, ': <span class="text-green-600">"string"</span>').replace(/: [0-9]+/g, ': <span class="text-amber-600">$&</span>') }} />
                                                </pre>
                                            </ScrollArea>
                                        </Card>
                                    </TabsContent>
                                </Tabs>
                            </div>



                            {/* Endpoint Item: Amendment Details */}
                            <div className="space-y-4 pt-8 border-t border-slate-100" id="endpoint-amendment-details">
                                <div className="flex items-center gap-4">
                                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 font-mono text-base px-3 py-1">GET</Badge>
                                    <code className="text-lg font-mono text-slate-700">/api/public/v1/amendments/:id</code>
                                </div>
                                <p className="text-slate-600">
                                    Retorna os detalhes completos de uma única emenda, incluindo seu <strong>histórico completo (linha do tempo)</strong>, anexos e status de retificação.
                                </p>

                                <Tabs defaultValue="response" className="w-full">
                                    <TabsList>
                                        <TabsTrigger value="response">Exemplo de Resposta</TabsTrigger>
                                        <TabsTrigger value="parameters">Parâmetros</TabsTrigger>
                                    </TabsList>
                                    <TabsContent value="parameters" className="mt-4">
                                        <Card className="bg-white border-slate-200">
                                            <div className="rounded-md border-b bg-slate-50 px-4 py-3 font-medium text-sm text-slate-700 grid grid-cols-12 gap-4">
                                                <div className="col-span-3">Parâmetro</div>
                                                <div className="col-span-2">Local</div>
                                                <div className="col-span-7">Descrição</div>
                                            </div>
                                            <div className="px-4 py-3 text-sm text-slate-600 grid grid-cols-12 gap-4">
                                                <div className="col-span-3 font-mono text-blue-600">:id</div>
                                                <div className="col-span-2">path</div>
                                                <div className="col-span-7">ID único da emenda (Firestore ID).</div>
                                            </div>
                                        </Card>
                                    </TabsContent>
                                    <TabsContent value="response" className="mt-4">
                                        <Card className="bg-slate-50 border-slate-200 shadow-sm">
                                            <ScrollArea className="h-[500px] w-full rounded-md">
                                                <pre className="p-4 text-sm font-mono leading-relaxed text-slate-800">
                                                    <code dangerouslySetInnerHTML={{
                                                        __html: JSON.stringify({
                                                            id: "xyz98765",
                                                            officialCode: "001-025-2024",
                                                            value: 50000,
                                                            description: "Aquisição de computadores...",
                                                            status: "Aprovada pelo Gestor",
                                                            dispatchStatus: "aprovada",
                                                            dispatchNote: "Documentação ok",
                                                            timeline: [
                                                                {
                                                                    "dateTime": "2024-03-25T14:30:00Z",
                                                                    "actorEmail": "gestor@sinop.mt.leg.br",
                                                                    "action": "Aprovação: Documentação ok",
                                                                    "changes": [
                                                                        { "field": "status", "from": "Pendente", "to": "Aprovada pelo Gestor" },
                                                                        { "field": "dispatchStatus", "from": null, "to": "aprovada" }
                                                                    ]
                                                                },
                                                                {
                                                                    "dateTime": "2024-03-20T10:00:00Z",
                                                                    "actorEmail": "cnpj-123...@instituicao.local",
                                                                    "action": null,
                                                                    "changes": [
                                                                        { "field": "detailingSaved", "from": false, "to": true },
                                                                        { "field": "status", "from": "Em andamento", "to": "Detalhamento enviado" }
                                                                    ]
                                                                }
                                                            ]
                                                        }, null, 2).replace(/"key":/g, '<span class="text-blue-600">"key":</span>').replace(/: "string"/g, ': <span class="text-green-600">"string"</span>').replace(/: [0-9]+/g, ': <span class="text-amber-600">$&</span>')
                                                    }} />
                                                </pre>
                                            </ScrollArea>
                                        </Card>
                                    </TabsContent>
                                </Tabs>
                            </div>

                            {/* Endpoint Item: Institutions */}
                            <div className="space-y-4 pt-8 border-t border-slate-100" id="endpoint-institutions">
                                <div className="flex items-center gap-4">
                                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 font-mono text-base px-3 py-1">GET</Badge>
                                    <code className="text-lg font-mono text-slate-700">/api/public/v1/institutions</code>
                                </div>
                                <p className="text-slate-600">
                                    Lista as instituições beneficiárias cadastradas, com suporte a busca por nome/CNPJ e filtro por categoria.
                                </p>

                                <Tabs defaultValue="parameters" className="w-full">
                                    <TabsList>
                                        <TabsTrigger value="parameters">Parâmetros</TabsTrigger>
                                        <TabsTrigger value="response">Exemplo de Resposta</TabsTrigger>
                                    </TabsList>
                                    <TabsContent value="parameters" className="mt-4">
                                        <Card className="bg-white border-slate-200">
                                            <div className="rounded-md border-b bg-slate-50 px-4 py-3 font-medium text-sm text-slate-700 grid grid-cols-12 gap-4">
                                                <div className="col-span-3">Parâmetro</div>
                                                <div className="col-span-2">Tipo</div>
                                                <div className="col-span-7">Descrição</div>
                                            </div>
                                            <div className="px-4 py-3 text-sm text-slate-600 grid grid-cols-12 gap-4 border-b">
                                                <div className="col-span-3 font-mono text-blue-600">category</div>
                                                <div className="col-span-2">string</div>
                                                <div className="col-span-7">Filtra pela categoria (ex: 'SAÚDE', 'EDUCAÇÃO').</div>
                                            </div>
                                            <div className="px-4 py-3 text-sm text-slate-600 grid grid-cols-12 gap-4">
                                                <div className="col-span-3 font-mono text-blue-600">search</div>
                                                <div className="col-span-2">string</div>
                                                <div className="col-span-7">Busca textual parcial por nome ou CNPJ (apenas números).</div>
                                            </div>
                                        </Card>
                                    </TabsContent>
                                    <TabsContent value="response" className="mt-4">
                                        <Card className="bg-slate-50 border-slate-200 shadow-sm">
                                            <ScrollArea className="h-[300px] w-full rounded-md">
                                                <pre className="p-4 text-sm font-mono leading-relaxed text-slate-800">
                                                    <code dangerouslySetInnerHTML={{ __html: JSON.stringify({ count: 1, filters: { category: "SAÚDE" }, data: [{ id: "...", name: "Hospital Santa Casa", cnpj: "12345678000100", category: "SAÚDE" }] }, null, 2).replace(/"key":/g, '<span class="text-blue-600">"key":</span>').replace(/: "string"/g, ': <span class="text-green-600">"string"</span>').replace(/: [0-9]+/g, ': <span class="text-amber-600">$&</span>') }} />
                                                </pre>
                                            </ScrollArea>
                                        </Card>
                                    </TabsContent>
                                </Tabs>
                            </div>

                            {/* Endpoint Item: Consolidated Data */}
                            <div className="space-y-4 pt-8 border-t border-slate-100" id="endpoint-data">
                                <div className="flex items-center gap-4">
                                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 font-mono text-base px-3 py-1">GET</Badge>
                                    <code className="text-lg font-mono text-slate-700">/api/public/v1/data</code>
                                </div>
                                <p className="text-slate-600">
                                    Retorna um objeto consolidado contendo TODO o dataset público (vereadores, instituições e emendas). Ideal para cargas iniciais ou sincronização completa.
                                </p>

                                <Tabs defaultValue="response" className="w-full">
                                    <TabsList>
                                        <TabsTrigger value="response">Exemplo de Resposta</TabsTrigger>
                                        <TabsTrigger value="parameters">Parâmetros</TabsTrigger>
                                    </TabsList>
                                    <TabsContent value="response" className="mt-4">
                                        <Card className="bg-slate-50 border-slate-200 shadow-sm">
                                            <ScrollArea className="h-[400px] w-full rounded-md">
                                                <pre className="p-4 text-sm font-mono leading-relaxed text-slate-800">
                                                    <code dangerouslySetInnerHTML={{ __html: ExampleResponse.replace(/"key":/g, '<span class="text-blue-600">"key":</span>').replace(/: "string"/g, ': <span class="text-green-600">"string"</span>').replace(/: true/g, ': <span class="text-purple-600">true</span>').replace(/: [0-9]+/g, ': <span class="text-amber-600">$&</span>') }} />
                                                </pre>
                                            </ScrollArea>
                                        </Card>
                                    </TabsContent>
                                    <TabsContent value="parameters" className="mt-4">
                                        <Card className="bg-white border-slate-200">
                                            <CardContent className="pt-6">
                                                <p className="text-sm text-slate-500 italic">Não há parâmetros de consulta (query params) para este endpoint atualmente.</p>
                                            </CardContent>
                                        </Card>
                                    </TabsContent>
                                </Tabs>
                            </div>
                        </section>

                        {/* Footer */}
                        <div className="border-t border-slate-200 pt-8 mt-12 text-center text-slate-500 text-sm">
                            <p>&copy; {new Date().getFullYear()} Sistema de Gestão de Emendas. Todos os direitos reservados.</p>
                        </div>

                    </div>
                </div >
            </main >
        </div >
    );
}
